import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Building2, GraduationCap, ArrowRight, Award,
  Briefcase, Star, Download, ShieldCheck, Sparkles, MapPin,
} from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import BrandLogos from "@/components/BrandLogos";
import Seo from "@/components/Seo";

const CERT_ICONS = { ShieldCheck, Sparkles, Award };

const TYPE_STYLE = {
  freelance: { color: "text-[var(--gold)]", bg: "bg-[var(--gold)]/10 border-[var(--gold)]/40" },
  permanent: { color: "text-blue-300", bg: "bg-blue-500/10 border-blue-500/40" },
  student: { color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/40" },
  leave: { color: "text-violet-300", bg: "bg-violet-500/10 border-violet-500/40" },
  temp: { color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/40" },
  academic: { color: "text-pink-300", bg: "bg-pink-500/10 border-pink-500/40" },
};

export default function ExperiencePage() {
  const { t, tl, pathWithLang } = useI18n();
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/experience").then((r) => setExperience(r.data || [])).catch(() => {});
    api.get("/education").then((r) => setEducation(r.data || [])).catch(() => {});
    api.get("/certifications").then((r) => setCertifications(r.data || [])).catch(() => {});
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="experience-page">
      <Seo title={t("seo.experience.title")} description={t("seo.experience.description")} />
      {/* HERO */}
      <section className="relative noise overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-hero-ambient" style={{ backgroundImage: "url(/visuals/hero-ambient.png)" }} />
          <div className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full bg-[var(--gold)] opacity-[0.05] blur-[120px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-5">
                · {t("exp.label")}
              </div>
              <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.05] tracking-[-0.035em] max-w-5xl">
                {t("exp.titleA")} <span className="font-serif gold-grad">{t("exp.titleAccent")}</span> {t("exp.titleEnd")}
              </h1>
              <p className="mt-8 text-lg text-[var(--text-1)] max-w-3xl leading-relaxed">
                {t("exp.intro")}
              </p>
            </div>

            {profile?.cv_pdf_de && (
              <div className="lg:col-span-4 lg:text-right">
                <a
                  href={profile.cv_pdf_de}
                  download
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cv-download-btn"
                  className="btn-primary"
                >
                  <Download size={14} />
                  {t("common.cvPdf")}
                </a>
                <div className="text-xs text-[var(--text-2)] mt-3 font-mono">
                  {t("exp.cvSince")}
                </div>
              </div>
            )}
          </div>

          {/* Stats strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
            <StatBlock
              icon={Briefcase}
              label={t("exp.stationsLabel")}
              value={experience.length || "—"}
              logos={["audi", "bmw", "cariad"]}
            />
            <StatBlock
              icon={Star}
              label={t("exp.yearsExpLabel")}
              value={t("exp.yearsExpValue")}
              logos={["bertrandt", "in-tech", "ic-consult"]}
            />
            <StatBlock
              icon={Award}
              label={t("exp.certsLabel")}
              value={certifications.length || "—"}
              logos={["istqb", "mit"]}
            />
            <StatBlock
              icon={GraduationCap}
              label={t("exp.studyLabel")}
              value={t("exp.studyValue")}
              logos={["lmu"]}
            />
          </div>
        </div>
      </section>

      {/* EXPERIENCE TIMELINE */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
            · {t("exp.workLabel")}
          </div>
          <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            {t("exp.workTitleA")} <span className="font-serif gold-grad">{t("exp.workAccent")}</span> {t("exp.workEnd")}
          </h2>
        </div>

        <div className="relative">
          <div className="absolute left-4 lg:left-[200px] top-0 bottom-0 w-px bg-[var(--line-soft)] hidden md:block" />

          {experience.map((exp, i) => {
            const tt = TYPE_STYLE[exp.type] || TYPE_STYLE.permanent;
            const expKey = exp.order ?? `${exp.period}-${i}`;
            return (
              <div
                key={expKey}
                data-testid={`experience-row-${i}`}
                className="relative grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 lg:gap-12 mb-6"
              >
                <div className="md:pt-2 md:pr-8 md:text-right relative">
                  <div className="font-mono text-xs uppercase tracking-wider text-[var(--gold)]">
                    {exp.period}
                  </div>
                  {exp.location && (
                    <div className="flex md:justify-end items-center gap-1.5 text-xs text-[var(--text-2)] mt-2 font-mono">
                      <MapPin size={11} />
                      <span>{tl(exp.location)}</span>
                    </div>
                  )}
                  <div className="hidden md:block absolute right-[-9px] top-3 w-4 h-4 rounded-full bg-[var(--bg-0)] border-2 border-[var(--gold)]" />
                </div>

                <div className="svc-card themed-card md:ml-8">
                  <div
                    aria-hidden="true"
                    className="themed-card-backdrop themed-card-backdrop--subtle"
                    style={{ backgroundImage: "url(/visuals/cards/timeline-automotive.png)" }}
                  />
                  <div aria-hidden="true" className="themed-card-overlay" />
                  <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Building2 size={14} className="text-[var(--gold)] shrink-0" />
                      <div className="text-[var(--text-2)] text-sm">{tl(exp.company)}</div>
                    </div>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 border rounded-full ${tt.bg} ${tt.color}`}
                    >
                      {t(`types.${exp.type}`) || exp.type}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl text-[var(--text-0)] leading-tight mb-2">
                    {tl(exp.role)}
                  </h3>
                  {exp.client && (
                    <div className="text-sm mb-4">
                      <span className="text-[var(--text-2)]">{t("exp.client")} </span>
                      <span className="text-[var(--gold)] font-medium">{tl(exp.client)}</span>
                    </div>
                  )}
                  {exp.highlights?.length > 0 && (
                    <ul className="space-y-2 mt-3">
                      {exp.highlights.map((h, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-3 text-[var(--text-1)] leading-relaxed text-[15px]"
                        >
                          <ArrowRight size={14} className="text-[var(--gold)] mt-1.5 shrink-0" />
                          <span>{tl(h)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {exp.tech?.length > 0 && (
                    <div className="mt-5 pt-5 border-t hairline">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--gold)] font-mono mb-3">
                        {t("exp.techStack")}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {exp.tech.map((s) => (
                          <span key={s} className="tag-pill">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CERTIFICATIONS */}
      {certifications.length > 0 && (
        <section id="certifications" className="border-t hairline bg-[var(--bg-1)]/40 scroll-mt-24">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
            <div className="mb-12 max-w-2xl">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("exp.certsHeading")}
              </div>
              <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                {t("exp.certsHeadingA")} <span className="font-serif gold-grad">{t("exp.certsHeadingAccent")}</span>{t("exp.certsHeadingEnd")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {certifications.map((c) => {
                const Icon = CERT_ICONS[c.icon] || Award;
                return (
                  <div key={c.key} data-testid={`cert-${c.key}`} className="svc-card themed-card flex items-start gap-5">
                    <div
                      aria-hidden="true"
                      className="themed-card-backdrop"
                      style={{ backgroundImage: "url(/visuals/cards/certs-parchment.png)" }}
                    />
                    <div aria-hidden="true" className="themed-card-overlay" />
                    <div className="w-14 h-14 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0 backdrop-blur-sm">
                      <Icon size={22} className="text-[var(--gold)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-xs text-[var(--gold)] uppercase tracking-wider">{c.year}</span>
                      </div>
                      <h3 className="font-display text-xl text-[var(--text-0)] leading-snug mb-1">{tl(c.name)}</h3>
                      <div className="text-sm text-[var(--text-2)] mb-3">{tl(c.issuer)}</div>
                      <p className="text-sm text-[var(--text-1)] leading-relaxed">{tl(c.description)}</p>
                      {c.pdf_url && (
                        <a
                          href={c.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          data-testid={`cert-download-${c.key}`}
                          className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--text-0)] transition-colors duration-200 group"
                        >
                          <Download size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                          <span>{t("exp.downloadCert")}</span>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* EDUCATION */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <div className="mb-12 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("exp.eduLabel")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              {t("exp.eduTitleA")} <span className="font-serif gold-grad">{t("exp.eduAccent")}</span>{t("exp.eduEnd")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {education.map((e, i) => (
              <div key={e.order ?? e.year ?? i} className="svc-card themed-card flex items-start gap-5">
                <div
                  aria-hidden="true"
                  className="themed-card-backdrop"
                  style={{ backgroundImage: "url(/visuals/cards/education-blueprint.png)" }}
                />
                <div aria-hidden="true" className="themed-card-overlay" />
                <div className="w-12 h-12 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0 backdrop-blur-sm">
                  <GraduationCap size={18} className="text-[var(--gold)]" />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-xs text-[var(--gold)] uppercase tracking-wider mb-1">
                    {e.year}
                  </div>
                  <div className="text-[var(--text-0)] font-display text-lg leading-snug mb-1">
                    {tl(e.title)}
                  </div>
                  <div className="text-[var(--text-2)] text-sm">{tl(e.place)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="border hairline-gold rounded p-10 lg:p-14 flex flex-col lg:flex-row gap-8 items-center justify-between bg-gradient-to-br from-[var(--bg-1)] to-[var(--bg-0)]">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight max-w-2xl">
              {t("exp.ctaTitleA")} <span className="font-serif gold-grad">{t("exp.ctaTitleAccent")}</span>{t("exp.ctaTitleEnd")}
            </h2>
            <p className="text-[var(--text-1)] mt-3 max-w-xl">
              {t("exp.ctaDesc")}
            </p>
          </div>
          <Link to={pathWithLang("/kontakt")} className="btn-primary" data-testid="exp-cta">
            {t("exp.ctaButton")} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, logos }) {
  return (
    <div className="svc-card p-5">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center shrink-0">
          <Icon size={18} className="text-[var(--gold)]" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-[var(--text-2)] mb-0.5">{label}</div>
          <div className="text-[var(--text-0)] font-display text-lg leading-snug">{value}</div>
        </div>
      </div>
      {logos && logos.length > 0 && (
        <>
          <div className="my-4 h-px bg-[var(--line-soft)]" />
          <BrandLogos items={logos} />
        </>
      )}
    </div>
  );
}
